// UI mode — Dev (technical detail visible) vs Prod (end-user view).
//
// Persisted in localStorage under "ai_ui_mode". Default "dev" for now;
// flip to "prod" before shipping to non-developer tenants.
//
// Changing the mode reconnects the socket so the server-side handshake
// picks up the new value (the handshake feeds buildSystemPrompt's
// uiMode option, which controls whether BLOCK_4 is appended to the
// system prompt). Mid-conversation is OK — the new mode applies on the
// next message because each turn rebuilds the prompt.

import React, { createContext, useContext, useState, useCallback } from "react";
import { refreshAuth } from "../services/chartAgentSocket";

const STORAGE_KEY = "ai_ui_mode";
const DEFAULT_MODE = "dev";

const UiModeContext = createContext({ uiMode: DEFAULT_MODE, setUiMode: () => {} });

export function UiModeProvider({ children }) {
  const [uiMode, setUiModeState] = useState(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    return stored === "prod" ? "prod" : DEFAULT_MODE;
  });

  const setUiMode = useCallback((next) => {
    const normalized = next === "prod" ? "prod" : "dev";
    setUiModeState(normalized);
    localStorage.setItem(STORAGE_KEY, normalized);
    // Reconnect so the new uiMode is picked up by the server's handshake
    // middleware; subsequent messages will get the matching system prompt.
    refreshAuth();
  }, []);

  return (
    <UiModeContext.Provider value={{ uiMode, setUiMode }}>
      {children}
    </UiModeContext.Provider>
  );
}

export function useUiMode() {
  return useContext(UiModeContext);
}
